/**
 * roles.ts — Static role → permission mapping.
 *
 * This is the RBAC policy engine. It maps each Role to the set of Permission
 * strings that role is granted. The mapping is baked into the app binary so
 * it works fully offline — no network or DB queries needed.
 *
 * For future per-user permission overrides (StaffAssignment.permissions JSON),
 * the usePermissions() hook will merge them on top of this base map.
 */

import {
  type Role,
  type Permission,
  ROLES,
  ALL_PERMISSIONS,
  // Dashboard
  DASHBOARD_VIEW,
  // Billing
  BILLING_CREATE,
  BILLING_VIEW,
  BILLING_VOID,
  BILLING_CREDIT_NOTE,
  BILLING_QUICK_BILL,
  // Invoices
  INVOICE_VIEW_ALL,
  INVOICE_VIEW_OWN,
  // Customers
  CUSTOMER_VIEW,
  CUSTOMER_CREATE,
  CUSTOMER_EDIT,
  CUSTOMER_STATEMENT,
  // Inventory
  INVENTORY_VIEW,
  INVENTORY_CREATE,
  INVENTORY_EDIT,
  INVENTORY_ADJUST_STOCK,
  INVENTORY_TRANSFER,
  INVENTORY_IMPORT,
  // Purchasing
  PURCHASING_VIEW,
  PURCHASING_CREATE_PO,
  PURCHASING_RECEIVE_PO,
  PURCHASING_SUPPLIER_MANAGE,
  PURCHASING_RECORD_PAYMENT,
  PURCHASING_CREATE_RETURN,
  // Reports
  REPORTS_VIEW,
  REPORTS_DAY_CLOSE,
  REPORTS_GST,
  REPORTS_EXPORT,
  // Settings
  SETTINGS_MANAGE,
  SETTINGS_STAFF_MANAGE,
  // Activity Log
  ACTIVITY_LOG_VIEW,
  // Commission
  STAFF_COMMISSION_VIEW,
  STAFF_COMMISSION_PAYOUT,
} from './permissions';

// ─── Role → Permission Map ──────────────────────────────────────────────────

const OWNER_PERMISSIONS: ReadonlySet<Permission> = new Set(ALL_PERMISSIONS);

const MANAGER_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  DASHBOARD_VIEW,
  // Billing — full operational
  BILLING_CREATE,
  BILLING_VIEW,
  BILLING_VOID,
  BILLING_CREDIT_NOTE,
  BILLING_QUICK_BILL,
  // Invoices
  INVOICE_VIEW_ALL,
  INVOICE_VIEW_OWN,
  // Customers — full
  CUSTOMER_VIEW,
  CUSTOMER_CREATE,
  CUSTOMER_EDIT,
  CUSTOMER_STATEMENT,
  // Inventory — full
  INVENTORY_VIEW,
  INVENTORY_CREATE,
  INVENTORY_EDIT,
  INVENTORY_ADJUST_STOCK,
  INVENTORY_TRANSFER,
  INVENTORY_IMPORT,
  // Purchasing — full
  PURCHASING_VIEW,
  PURCHASING_CREATE_PO,
  PURCHASING_RECEIVE_PO,
  PURCHASING_SUPPLIER_MANAGE,
  PURCHASING_RECORD_PAYMENT,
  PURCHASING_CREATE_RETURN,
  // Reports — all except export
  REPORTS_VIEW,
  REPORTS_DAY_CLOSE,
  REPORTS_GST,
  // Activity Log — oversight
  ACTIVITY_LOG_VIEW,
  // Commission
  STAFF_COMMISSION_VIEW,
  STAFF_COMMISSION_PAYOUT,
]);

const CASHIER_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  DASHBOARD_VIEW,
  // Billing — create only, no void/credit-note
  BILLING_CREATE,
  BILLING_VIEW,
  // Invoices — own only
  INVOICE_VIEW_OWN,
  // Customers — view only
  CUSTOMER_VIEW,
  // Commission — view own only
  STAFF_COMMISSION_VIEW,
]);

const ACCOUNTANT_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  DASHBOARD_VIEW,
  // Billing — view + credit note, no create/void
  BILLING_VIEW,
  BILLING_CREDIT_NOTE,
  // Invoices
  INVOICE_VIEW_ALL,
  INVOICE_VIEW_OWN,
  // Customers — view + statements
  CUSTOMER_VIEW,
  CUSTOMER_STATEMENT,
  // Purchasing — view only
  PURCHASING_VIEW,
  // Reports — full including export
  REPORTS_VIEW,
  REPORTS_GST,
  REPORTS_EXPORT,
  // Commission — view + payout
  STAFF_COMMISSION_VIEW,
  STAFF_COMMISSION_PAYOUT,
]);

const INVENTORY_MANAGER_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  DASHBOARD_VIEW,
  // Inventory — full
  INVENTORY_VIEW,
  INVENTORY_CREATE,
  INVENTORY_EDIT,
  INVENTORY_ADJUST_STOCK,
  INVENTORY_TRANSFER,
  INVENTORY_IMPORT,
  // Purchasing — full except payments
  PURCHASING_VIEW,
  PURCHASING_CREATE_PO,
  PURCHASING_RECEIVE_PO,
  PURCHASING_SUPPLIER_MANAGE,
  PURCHASING_CREATE_RETURN,
]);

const VIEWER_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  DASHBOARD_VIEW,
  BILLING_VIEW,
  INVOICE_VIEW_ALL,
  CUSTOMER_VIEW,
  INVENTORY_VIEW,
  PURCHASING_VIEW,
]);

/**
 * Master lookup: Role → Set<Permission>.
 * Every role defined in ROLES must have an entry here.
 */
export const ROLE_PERMISSIONS: Readonly<Record<Role, ReadonlySet<Permission>>> = {
  [ROLES.OWNER]: OWNER_PERMISSIONS,
  [ROLES.MANAGER]: MANAGER_PERMISSIONS,
  [ROLES.CASHIER]: CASHIER_PERMISSIONS,
  [ROLES.ACCOUNTANT]: ACCOUNTANT_PERMISSIONS,
  [ROLES.INVENTORY_MANAGER]: INVENTORY_MANAGER_PERMISSIONS,
  [ROLES.VIEWER]: VIEWER_PERMISSIONS,
};

// ─── Helper Functions ────────────────────────────────────────────────────────

/**
 * Get the full permission set for a role.
 * Returns empty set for unknown roles (defensive — never crash).
 */
export function getPermissionsForRole(role: string): ReadonlySet<Permission> {
  return ROLE_PERMISSIONS[role as Role] ?? new Set();
}

/**
 * Check if a role has a specific permission.
 */
export function hasPermission(role: string, permission: Permission): boolean {
  return getPermissionsForRole(role).has(permission);
}

/**
 * Check if a role has ANY of the given permissions (OR logic).
 */
export function hasAnyPermission(role: string, permissions: Permission[]): boolean {
  const rolePerms = getPermissionsForRole(role);
  return permissions.some(p => rolePerms.has(p));
}

/**
 * Check if a role has ALL of the given permissions (AND logic).
 */
export function hasAllPermissions(role: string, permissions: Permission[]): boolean {
  const rolePerms = getPermissionsForRole(role);
  return permissions.every(p => rolePerms.has(p));
}

// ─── Drawer Visibility Map ──────────────────────────────────────────────────
// Maps each drawer section to the permission(s) needed to see it.
// Used by the navigation layer to filter drawer items.

export const DRAWER_PERMISSIONS: Record<string, Permission[]> = {
  Dashboard: [DASHBOARD_VIEW],
  Billing: [BILLING_CREATE, BILLING_VIEW],
  Invoices: [INVOICE_VIEW_ALL, INVOICE_VIEW_OWN],
  UsersStaff: [CUSTOMER_VIEW, SETTINGS_STAFF_MANAGE],
  Inventory: [INVENTORY_VIEW],
  Reports: [REPORTS_VIEW],
  ActivityLog: [ACTIVITY_LOG_VIEW],
  SettingsStore: [SETTINGS_MANAGE],
};
